import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
  RouterScrollBehavior
} from 'vue-router'

import { MODULE_AUFGABEN_COLOR } from '@/modules/moduleConstants'
import AdministrationRoutes from '@/router/modules/administrationRoutes'
import AnalysenRoutes from '@/router/modules/analysenRoutes'
import ArbeitsmittelRoutes from '@/router/modules/arbeitsmittelRoutes'
import AufgabenRoutes from '@/router/modules/aufgabenRoutes'
import BerichteRoutes from '@/router/modules/berichteRoutes'
import DateienRoutes from '@/router/modules/dateienRoutes'
import EmailingRoutes from '@/router/modules/emailingRoutes'
import HistoryRoutes from '@/router/modules/historyRoutes'
import KompetenzenRoutes from '@/router/modules/kompetenzenRoutes'
import KonsoleRoutes from '@/router/modules/konsoleRoutes'
import KostenRoutes from '@/router/modules/kostenRoutes'
import LabelsRoutes from '@/router/modules/labelsRoutes'
import LiegenschaftenRoutes from '@/router/modules/liegenschaftenRoutes'
import ManagementRoutes from '@/router/modules/managementRoutes'
import MitarbeiterRoutes from '@/router/modules/mitarbeiterRoutes'
import NotizenRoutes from '@/router/modules/notizenRoutes'
import OrganisationRoutes from '@/router/modules/organisationRoutes'
import PraeventionRoutes from '@/router/modules/praeventionRoutes'
import ProjekteRoutes from '@/router/modules/projekteRoutes'
import SchulungsorganisationRoutes from '@/router/modules/schulungsorganisationRoutes'
import StammdatenRoutes from '@/router/modules/stammdatenRoutes'
import TicketsystemRoutes from '@/router/modules/ticketsystemRoutes'
import VertriebRoutes from '@/router/modules/vertriebRoutes'
import VerwaltungRoutes from '@/router/modules/verwaltungRoutes'
import { useAppViewStore } from '@/stores/appView'
import { useFormStore } from '@/stores/form'
import { useItemSelectionStore } from '@/stores/itemSelection'
import { useNavigationStore } from '@/stores/navigation'
import { usePendingRouteStore } from '@/stores/pendingRoute'
import { useProgressStore } from '@/stores/progress'
import { useRecordNavigationStore } from '@/stores/recordNavigation'
import { hasToken } from '@/utils/authToken'
import { obersterDialog } from '@/utils/dialogStack'
import logging from '@/utils/logging.ts'

const BaseLogin = () => import('@/base/BaseLogin.vue')
const BaseLogout = () => import('@/base/BaseLogout.vue')
const BasePageFreigabeNotFound = () => import('@/base/BasePageFreigabeNotFound.vue')
const BasePageNotFound = () => import('@/base/BasePageNotFound.vue')
const BasePermissionDenied = () => import('@/base/BasePermissionDenied.vue')
const BaseAppLayout = () => import('@/base/BaseAppLayout.vue')
const DashboardWrapper = () => import('@/modules/dashboard/DashboardWrapper.vue')
const Importvorlagen = () => import('@/modules/administration/Importvorlagen.vue')
const PasswortReset = () => import('@/modules/verwaltung/PasswortReset.vue')
const AusbildungsnachweiseOeffentlich = () => import('@/modules/mitarbeiter/AusbildungsnachweiseOeffentlich.vue')
const ArbeitsmittelUnterlagenOeffentlich = () => import('@/modules/arbeitsmittel/ArbeitsmittelUnterlagenOeffentlich.vue')
const GefahrstoffUnterlagenOeffentlich = () => import('@/modules/praevention/GefahrstoffUnterlagenOeffentlich.vue')
const OeffentlicheKurse = () => import('@/modules/schulungsorganisation/OeffentlicheKurse.vue')
const OeffentlicheKurseAnmeldung = () => import('@/modules/schulungsorganisation/OeffentlicheKurseAnmeldung.vue')
const OeffentlicheVisitenAnmeldung = () => import('@/modules/mitarbeiter/OeffentlicheVisitenAnmeldung.vue')
const AnwesenheitsregisterDigitalView = () => import('@/modules/schulungsorganisation/AnwesenheitsregisterDigitalView.vue')
const AnwesenheitsregisterDigitalTeilnehmerView = () => import('@/modules/schulungsorganisation/AnwesenheitsregisterDigitalTeilnehmerView.vue')
const PruefungOeffentlichView = () => import('@/modules/schulungsorganisation/PruefungOeffentlichView.vue')
const UnterlagenFreigabenOeffentlich = () => import('@/modules/dateien/UnterlagenFreigabenOeffentlich.vue')

const updateFormProps = (route) => {
  return {
    id: parseInt(route.params.id)
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/login',
    name: 'BaseAppLayout',
    component: BaseAppLayout,
    children: [
      {
        path: '/permissiondenied',
        name: 'PermissionDenied',
        component: BasePermissionDenied
      },
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: DashboardWrapper,
        meta: { icon: 'mdi-home-outline', color: MODULE_AUFGABEN_COLOR }
      },
      {
        path: '/dashboards/dashboards/:id',
        name: 'DashboardDetail',
        component: DashboardWrapper,
        props: updateFormProps,
        meta: { icon: 'mdi-home-outline', color: MODULE_AUFGABEN_COLOR }
      },
      { path: '/importvorlagen', name: 'Importvorlagen', component: Importvorlagen },

      ...AdministrationRoutes,
      ...AnalysenRoutes,
      ...ArbeitsmittelRoutes,
      ...AufgabenRoutes,
      ...BerichteRoutes,
      ...DateienRoutes,
      ...EmailingRoutes,
      ...KompetenzenRoutes,
      ...KonsoleRoutes,
      ...KostenRoutes,
      ...SchulungsorganisationRoutes,
      ...LabelsRoutes,
      ...LiegenschaftenRoutes,
      ...ManagementRoutes,
      ...MitarbeiterRoutes,
      ...NotizenRoutes,
      ...OrganisationRoutes,
      ...PraeventionRoutes,
      ...ProjekteRoutes,
      ...StammdatenRoutes,
      ...TicketsystemRoutes,
      ...VertriebRoutes,
      ...VerwaltungRoutes,
      ...HistoryRoutes
    ],
  },
  {
    path: '/verwaltung/reset/:token',
    name: 'PasswortReset',
    component: PasswortReset,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: BaseLogin,
    meta: { requiresAuth: false }
  },
  {
    path: '/logout',
    name: 'Logout',
    component: BaseLogout
  },
  {
    path: '/freigabe404',
    name: 'FreigabeNotFound',
    component: BasePageFreigabeNotFound,
    meta: { requiresAuth: false }
  },
  // Unterlagen-Freigaben
  {
    path: '/unterlagendokumente/:uuid/',
    name: 'UnterlagenFreigabenOeffentlich',
    component: UnterlagenFreigabenOeffentlich,
    props: true,
    meta: { requiresAuth: false }
  },
  // Ausbildungsdokumente oeffentlich
  {
    path: '/ausbildungsnachweiseoeffentlich/:hash/',
    name: 'AusbildungsnachweiseOeffentlich',
    component: AusbildungsnachweiseOeffentlich,
    props: true,
    meta: { requiresAuth: false }
  },
  // Sicherheitsplanunterlagen oeffentlich
  {
    path: '/projektunterlagenoeffentlich/:hash/',
    // Umleitung von alten Projektunterlagen auf neue UnterlagenFreigaben
    redirect: to => {
      return { name: 'UnterlagenFreigabenOeffentlich', params: { uuid: to.params.hash }}
    },
    //name: 'ProjektUnterlagenOeffentlich',
    //component: ProjektUnterlagenOeffentlich,
    //props: true,
    //meta: { requiresAuth: false }
  },
  // Arbeitsmittelunterlagen oeffentlich
  {
    path: '/arbeitsmittel/arbeitsmittel/:id/arbeitsmittel_unterlagen_oeffentlich',
    name: 'ArbeitsmittelUnterlagenOeffentlich',
    component: ArbeitsmittelUnterlagenOeffentlich,
    props: true,
    meta: { requiresAuth: false }
  },
  // Gefahrstoffunterlagen oeffentlich
  {
    path: '/praevention/gefahrstoffe/:id/gefahrstoff_unterlagen_oeffentlich',
    name: 'GefahrstoffUnterlagenOeffentlich',
    component: GefahrstoffUnterlagenOeffentlich,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/visitenanmeldung/:hash/',
    name: 'OeffentlicheVisitenAnmeldung',
    props: true,
    component: OeffentlicheVisitenAnmeldung,
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: BasePageNotFound
  },
  // Öffentliche Schulungen Seite
  {
    path: '/schulungsorganisation/oeffentlicheschulungen/',
    name: 'OeffentlicheKurse',
    component: OeffentlicheKurse,
    meta: { requiresAuth: false }
  },
  {
    path: '/schulungsorganisation/oeffentlicheschulungen/:typ/:id',
    name: 'OeffentlicheKurseAnmeldung',
    component: OeffentlicheKurseAnmeldung,
    props: true,
    meta: { requiresAuth: false }
  },
  // Digitales Anwesenheitsregister
  {
    path: '/schulungsorganisation/anwesenheitsregister/:uuid/',
    name: 'AnwesenheitsregisterDigital',
    component: AnwesenheitsregisterDigitalView,
    props: true,
    meta: { requiresAuth: false }
  },
  // Öffentliche Prüfung (Teilnehmer füllt per QR-Code aus)
  {
    path: '/schulungsorganisation/pruefung/:uuid/',
    name: 'PruefungOeffentlich',
    component: PruefungOeffentlichView,
    props: true,
    meta: { requiresAuth: false }
  },
  // Öffentliches Feedback (dieselbe View; erkennt den Typ aus den geladenen Daten)
  {
    path: '/schulungsorganisation/feedback/:uuid/',
    name: 'FeedbackOeffentlich',
    component: PruefungOeffentlichView,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/schulungsorganisation/anwesenheitsregister/teilnehmer/:token/',
    name: 'AnwesenheitsregisterDigitalTeilnehmer',
    component: AnwesenheitsregisterDigitalTeilnehmerView,
    props: true,
    meta: { requiresAuth: false }
  },
]
// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
const scrollBehavior: RouterScrollBehavior = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  savedPosition: ScrollToOptions | null
) => {
  if (savedPosition) {
    // savedPosition is only available for popstate navigations.
    // logging.info('return saved position')
    const recordNavigationStore = useRecordNavigationStore()

    // Vor/Zurück Positionen wiederherstellen
    // z.b. bei zuruck von der BilderCard Form
    if (to && to.path) {
      recordNavigationStore.loadPreviousRecord(to.path)
    }

    // debugger
    return savedPosition
  } else {
    const position: ScrollToOptions = {}

    // scroll to anchor by returning the selector
    if (to.hash) {

      const hashes = ['#dokumente', '#bilder', '#aufgaben']
      if (hashes.includes(to.hash)) {
        position.top = 150
      }

      if (document.querySelector(to.hash)) {
        return position
      }

      // fallback is scroll to top
      position.left = 0
      position.top = 0
      return position
    }

    return new Promise(() => {
      // check if any matched route config has meta that requires scrolling to top
      if (to.matched.some(m => m.meta.scrollToTop)) {
        // coords will be used if no selector is provided,
        // or if the selector didn't match any element.
        position.left = 0
        position.top = 0
      }
    })
  }
}

// Muss vor createRouter stehen: createWebHashHistory() haengt dort seinen eigenen popstate-Listener
// an, und Vue Router startet die Navigation samt Guards noch waehrend dieses Listeners. Ein spaeter
// registrierter Listener setzt das Flag erst, nachdem der Guard schon gelaufen ist.
let zurueckGedrueckt = false
window.addEventListener('popstate', (event) => {
  // Ein echter History-Sprung traegt immer den Router-Zustand. Ein reiner Wechsel des Fragments
  // (etwa eine von aussen gesetzte URL) hat keinen und ist kein Zurueck - sonst verwirft der Guard
  // unten eine Navigation, die gar keine Geste war.
  if (!event.state) return
  zurueckGedrueckt = true
  setTimeout(() => { zurueckGedrueckt = false })
})

const router = createRouter({
  history: createWebHashHistory(import.meta.env.NODE_ENV === 'production' ? (import.meta.env.VITE_APP_URL_PREPEND || '/') : '/'),
  scrollBehavior: scrollBehavior,
  routes: routes
})

// Zurück bei offenem Dialog ist eine Dialog-Geste, keine Navigation: der oberste Dialog schließt,
// die Navigation wird verworfen. Dieser Guard muss vor dem pendingRoute-Guard weiter unten stehen,
// sonst merkt der sich das Ziel und holt es nach dem Verwerfen nach.
router.beforeEach(() => {
  if (!zurueckGedrueckt) return undefined
  const dialog = obersterDialog()
  if (!dialog) return undefined
  try {
    // Die Navigation ist verworfen, also verbraucht kein Layout die Rückwärts-Markierung.
    useNavigationStore().setBrowserBackForwardUsed(false)
  } catch {
    // Beim allerersten Seitenaufruf ist Pinia noch nicht installiert — dann ist auch kein Dialog offen.
  }
  if (!dialog.blocked?.()) dialog.close()
  return false
})

// Navigation Guard, um URLs von /praevention/* auf /analysen/* umzuleiten
router.beforeEach((to) => {
  if (!to.path.startsWith('/praevention/')) {
    // Wenn die URL nicht mit /praevention/ beginnt, kann normal weitergemacht werden
    return
  }
  if (to.path.startsWith('/praevention/abfrageliste/')) {
    // Abfrageliste muss speziell umgleitet werden, da das model auch anders heist - Analysenvorlage
    const toPath = to.path
    const newPath = to.path.replace(/^\/praevention\/abfrageliste/, '/analysen/analysenvorlagen')
    logging.info(`PRAEVENTION wird auf ANALYSE umgeleitet: ${toPath} => ${newPath}`)
    return { path: newPath, query: to.query, hash: to.hash }
  }
  if (
    to.path.startsWith('/praevention/analysen/') ||
    to.path.startsWith('/praevention/bewertungsmethode/') ||
    to.path.startsWith('/praevention/bewertung/') ||
    to.path.startsWith('/praevention/bewertungsmatrix/') ||
    to.path.startsWith('/praevention/analysenmatrix/') ||
    to.path.startsWith('/praevention/berechnung/') ||
    to.path.startsWith('/praevention/einflussfaktor/') ||
    to.path.startsWith('/praevention/efwerte/') ||
    to.path.startsWith('/praevention/analysenmatrix/') ||
    to.path.startsWith('/praevention/analysenmatrix/')
  ) {
    // Diese Models heisen in der neuen App gleich und es muss nur /praevention mit /analysen getauscht werden
    const toPath = to.path
    const newPath = to.path.replace(/^\/praevention/, '/analysen')
    logging.info(`PRAEVENTION wird auf ANALYSE umgeleitet: ${toPath} => ${newPath}`)
    return { path: newPath, query: to.query, hash: to.hash }
  }
  if (to.path.startsWith('/praevention/abfragegruppe/')) {
    // Abfragegruppe muss speziell umgleitet werden, da das model auch anders heist,
    // bzw nicht mehr als Gruppe gespeichert ist, sondern als Anforderungsdetail - Anforderungsdetail
    const toPath = to.path
    const newPath = to.path.replace(/^\/praevention\/abfragegruppe/, '/analysen/anforderungsdetail')
    logging.info(`PRAEVENTION wird auf ANALYSE umgeleitet: ${toPath} => ${newPath}`)
    return { path: newPath, query: to.query, hash: to.hash }
  }
  // Andernfalls geht es normal weiter
})

router.beforeEach((to) => {
  let requiresAuth = true
  if (to.meta && 'requiresAuth' in to.meta) {
    requiresAuth = to.meta.requiresAuth as boolean
  }
  if (requiresAuth) {
    if (!hasToken()) {
      return {
        path: '/login',
        query: { redirect: to.fullPath, token: to.query.token }
      }
    }
  }
})

router.beforeEach((to) => {
  let pendingRouteStore: ReturnType<typeof usePendingRouteStore> | undefined
  let formStore: ReturnType<typeof useFormStore> | undefined
  try {
    formStore = useFormStore()
    pendingRouteStore = usePendingRouteStore()
  } catch {
    //Exception ist okay, siehe Kommentar unten
  }

  // Wenn noch Dokumente beim Hochladen sind blockieren
  if (formStore && formStore.isDocumentUploadInProgress) {
    // logging.debug(`Blockiere Navigation nach ${to.path} wegen Dokumentenupload`)
    pendingRouteStore?.setPendingRoute(to)
    formStore.showDocumentUploadInProgressDialog()
    return false
  }
  if (formStore && formStore.hasChangedData) {
    // logging.debug(`Blockiere Navigation nach ${to.path} wegen ungespeicherten Daten`)
    pendingRouteStore?.setPendingRoute(to)
    formStore.showUnsavedWarning()
    return false
  }

  try {
    //AppView zurücksetzen
    const appViewStore = useAppViewStore()
    appViewStore.setShowFloatButton(false)
    appViewStore.resetSearchCardQueries()

    // Navigation zurücksetzen
    const navigationStore = useNavigationStore()
    navigationStore.clearOptionalNavigationItems()
    const progressStore = useProgressStore()
    progressStore.finish()
  } catch {
    /*
      Diese Exception ist okay.
      Beim allerersten Seitenaufruf ist Pinia noch nicht installiert und deswegen crasht useNavigationStore()
      Da zu diesem Zeitpunkt aber noch keine NavigationItems gesetzt sind, ist das okay
     */
    // logging.debug(e)
  }

})

router.afterEach((to, from, failure) => {
  if (!failure) {
    // logging.debug('reset Pending Route nach erfolgreicher Navigation')
    const pendingRouteStore = usePendingRouteStore()
    pendingRouteStore.resetPendingRoute()

    // Selektionen bei Route-Wechsel leeren, um Store-Akkumulation zu verhindern
    if (to.path !== from.path) {
      const itemSelectionStore = useItemSelectionStore()
      itemSelectionStore.clearAllItemSelections()
    }
  }
  sessionStorage.setItem('browser_history_available', String(true))
})

// Native Browser-Warnung anzeigen und aufräumen, wenn der Benutzer die Seite verlässt
window.addEventListener('beforeunload', function (event) {
  const appViewStore = useAppViewStore()
  const formStore = useFormStore()
  if (formStore.isDocumentUploadInProgress) {
    event.returnValue = 'nananana'
  }
  appViewStore.resetSearchCardQueries()
  appViewStore.setShowFloatButton(false)
  if (formStore.hasChangedData) {
    event.returnValue = 'nananana'
  }
})

export default router
